import * as React from "react";
import { Modal, Tabs } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { WorkloadDefaultOptions } from "./defaultOptions";
import { WorkloadLocalOptions } from "./localOptions";
import { v4 as uuidv4 } from "uuid";
import { store } from "../../../store";
import { cloneDeep } from "lodash";
import { formSetValue, formTriggerValidation, useCpFormValue } from "../../gvc/testNewForm/utils";
import { TabLabel } from "./tabLabel";
import { useCpFormContext } from "../../../components/form/useCpFormContext";
import { GvcLocationOption } from "./GvcLocationFetcher";

interface Props {}
export const WorkloadOptionsTabs: React.FC<Props> = ({}) => {
  const { id: formId } = useCpFormContext();
  const localOptions = useCpFormValue(`localOptions`);
  const activeKeyOption = useCpFormValue(`meta.activeKeyOption`);
  const gvcLocations: GvcLocationOption[] = useCpFormValue(`meta.gvcLocations`) || [];

  const selectedLocations = new Set(localOptions.map((lo: any) => lo.location).filter(Boolean));
  const unselectedLocationCount = gvcLocations.filter((loc) => !selectedLocations.has(loc.value)).length;
  const emptyLocalOptionsCount = localOptions.filter((lo: any) => !lo.location).length;
  const allLocationsUsed = gvcLocations.length > 0 && unselectedLocationCount <= emptyLocalOptionsCount;

  const onChange = (newActiveKey: string) => {
    formSetValue(formId, `meta.activeKeyOption`, newActiveKey);
  };

  const add = () => {
    const form = store.getState().form.instances[formId];
    const values: any = form.values;

    const _defaultOptions = cloneDeep(values.defaultOptions);

    const id = uuidv4();

    const localOptions = values.localOptions;

    // Auto-select the location if only one unselected remains
    const allGvcLocations: GvcLocationOption[] = values.meta?.gvcLocations || [];
    const selectedLocs = new Set((localOptions || []).map((lo: any) => lo.location).filter(Boolean));
    const unselectedLocs = allGvcLocations.filter((loc) => !selectedLocs.has(loc.value));
    const autoLocation = unselectedLocs.length === 1 ? unselectedLocs[0].value : "";

    const _items = cloneDeep(localOptions);
    _items.push({
      ...cloneDeep(_defaultOptions),
      id: id,
      location: autoLocation,
    });
    formSetValue(formId, `localOptions`, _items);

    formSetValue(formId, `meta.activeKeyOption`, `local-options-${id}`);

    formTriggerValidation(formId);
  };

  const tryRemove = (targetKeyStr: React.MouseEvent | React.KeyboardEvent | string) => {
    Modal.confirm({
      title: "Remove Local Options",
      content: "You will lose the configuration you set up for this location, are you sure that you want to remove it?",
      onOk: () => remove(targetKeyStr),
    });
  };

  const remove = (targetKey: React.MouseEvent | React.KeyboardEvent | string) => {
    const form = store.getState().form.instances[formId];
    const values: any = form.values;

    const localOptions = values.localOptions;
    let _items = cloneDeep(localOptions);
    const targetLocalOptionId = (targetKey as string).split(`local-options-`)[1];
    _items = _items.filter((i) => i.id !== targetLocalOptionId);

    let newActiveKey = "defaultOptions";
    if (targetKey !== activeKeyOption) {
      newActiveKey = activeKeyOption;
    }
    formSetValue(formId, `meta.activeKeyOption`, newActiveKey);
    formSetValue(formId, `localOptions`, _items);

    formTriggerValidation(formId);
  };

  const onEdit = (targetKey: React.MouseEvent | React.KeyboardEvent | string, action: "add" | "remove") => {
    if (action === "add") {
      add();
    } else {
      tryRemove(targetKey);
    }
  };

  return (
    <>
      <div className="cpln-form-section cpln-ant-tabs-container cpln-ant-card-container">
        <Tabs
          type={"editable-card"}
          items={[
            {
              key: "defaultOptions",
              label: <TabLabel label={`Default Options`} names={[`defaultOptions`]} showDirty />,
              children: <WorkloadDefaultOptions />,
              closable: false,
            },
            ...localOptions.map((localOption, index) => {
              const label = localOption.location || "Select Location";

              return {
                key: `local-options-${localOption.id}`,
                label: <TabLabel label={label} names={[`localOptions.${index}`]} showDirty />,
                children: <WorkloadLocalOptions key={`local-options-${localOption.id}`} index={index} />,
                closable: true,
              };
            }),
          ]}
          onChange={onChange}
          activeKey={activeKeyOption}
          onEdit={onEdit}
          hideAdd={allLocationsUsed}
          addIcon={
            <div className="flex items-center gap-2 px-4">
              <div>Add Local Options</div>
              <PlusOutlined />
            </div>
          }
        />
      </div>
    </>
  );
};
